const DEFAULT_AVATAR_URL = "img/blank_bubble.png";

export interface GeneratedAvatar {
  id: string;
  dataUrl: string;
  backgroundColor: string;
  label: string;
}

/**
 * Loads an image and returns a promise that resolves when the image is loaded
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Generates avatar variants with different background colors
 */
export async function generateAvatarVariants(primaryBackground: string, secondaryBackground: string): Promise<GeneratedAvatar[]> {
  const baseImage = await loadImage(DEFAULT_AVATAR_URL);

  const variants: GeneratedAvatar[] = [
    {
      id: "primary",
      backgroundColor: primaryBackground,
      label: "Primary Color",
      dataUrl: ""
    },
    {
      id: "secondary",
      backgroundColor: secondaryBackground,
      label: "Secondary Color",
      dataUrl: ""
    }
  ];

  for (const variant of variants) {
    variant.dataUrl = await generateAvatarWithBackground(baseImage, variant.backgroundColor);
  }

  return variants;
}

/**
 * Generates a single avatar with the specified background color
 */
async function generateAvatarWithBackground(baseImage: HTMLImageElement, backgroundColor: string): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = baseImage.width;
  canvas.height = baseImage.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(baseImage, 0, 0);

  return canvas.toDataURL("image/png");
}

/**
 * Converts a data URL to a File object for uploading
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}
